import React, { useState, useEffect, useContext, useRef } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import Cookies from "js-cookie";
import { DirectionContext } from "../App";
import API_ENDPOINTS from "../config/api";

const AdditionalEvidencePage = () => {
  const { navigateTo } = useContext(DirectionContext);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerificationRequested, setIsVerificationRequested] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [timer, setTimer] = useState(180); // 인증번호 유효 3분
  const [resendTimer, setResendTimer] = useState(0); // 재전송 쿨다운(60초)
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showRateLimitModal, setShowRateLimitModal] = useState(false);
  const [rateLimitMessage, setRateLimitMessage] = useState("");
  const [showNotEligibleModal, setShowNotEligibleModal] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const phoneInputRef = useRef(null);
  const verificationInputRef = useRef(null);

  const isValidPhone =
    phoneNumber.length === 11 && phoneNumber.startsWith("010");
  const showPhoneFormatError =
    phoneNumber.length > 0 && !phoneNumber.startsWith("010");

  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  // 인증번호 유효시간 카운트다운
  useEffect(() => {
    let interval;
    if (isVerificationRequested && !isVerified && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [isVerificationRequested, isVerified, timer]);

  // 재전송 쿨다운 카운트다운
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const formatTime = () => {
    const minutes = Math.floor(timer / 60);
    const seconds = timer % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  const handlePhoneNumberChange = (e) => {
    if (isVerified) return;
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 11);
    setPhoneNumber(value);
    if (value.length === 11 && phoneInputRef.current) {
      phoneInputRef.current.blur();
    }
  };

  const sendOtp = async () => {
    const response = await fetch(`${API_ENDPOINTS.BASE_URL}/auth/send-otp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        phoneNumber: phoneNumber,
        applicationType: "INDIVIDUAL",
      }),
    });

    if (response.status === 429) {
      const data = await response.json().catch(() => ({}));
      setRateLimitMessage(
        data?.message || "요청 제한을 초과했습니다. 잠시 후 다시 시도해주세요.",
      );
      setShowRateLimitModal(true);
      return false;
    }

    if (response.status !== 201 && !response.ok) {
      const data = await response.json().catch(() => ({}));
      const msg = data?.message || "인증번호 전송에 실패했습니다.";
      setErrorMessage(msg);
      showToastMessage(msg);
      return false;
    }

    return true;
  };

  const handleRequestVerification = async () => {
    if (!isValidPhone || isSubmitting) return;
    setIsSubmitting(true);
    setErrorMessage("");
    setVerificationFailed(false);
    setVerificationCode("");

    const success = await sendOtp();
    if (success) {
      setIsVerificationRequested(true);
      setTimer(180);
      setResendTimer(15);
      setTimeout(() => verificationInputRef.current?.focus(), 100);
    }
    setIsSubmitting(false);
  };

  const handleResendVerification = async () => {
    if (resendTimer > 0 || isSubmitting) return;
    setIsSubmitting(true);
    setErrorMessage("");
    setVerificationFailed(false);
    setVerificationCode("");

    const success = await sendOtp();
    if (success) {
      setTimer(180);
      setResendTimer(15);
      setTimeout(() => verificationInputRef.current?.focus(), 100);
    }
    setIsSubmitting(false);
  };

  const handleVerify = async () => {
    if (verificationCode.length !== 6 || isSubmitting) return;
    setIsSubmitting(true);
    setErrorMessage("");
    setVerificationFailed(false);

    try {
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/auth/verify-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phoneNumber: phoneNumber,
            code: verificationCode,
          }),
        },
      );

      const data = await response.json().catch(() => ({}));

      if (response.status === 429) {
        setRateLimitMessage(
          data?.message ||
            "잘못된 인증번호 시도 횟수가 초과되었습니다. 새로운 인증번호를 요청해주세요.",
        );
        setShowRateLimitModal(true);
        return;
      }

      if (!response.ok || !data.verified) {
        setVerificationFailed(true);
        showToastMessage("인증에 실패했습니다. 인증번호를 다시 확인해주세요.");
        return;
      }

      const applications = Array.isArray(data.application)
        ? data.application
        : data.application
          ? [data.application]
          : [];

      const eligibleApplication = applications.find(
        (app) => app?.lawsuitId === 3 && app?.status === "PAYMENT_COMPLETED",
      );

      if (!eligibleApplication) {
        setShowNotEligibleModal(true);
        return;
      }

      if (!data.token) {
        setVerificationFailed(true);
        return;
      }

      const meResponse = await fetch(`${API_ENDPOINTS.BASE_URL}/clients/me`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${data.token}`,
        },
      });

      if (!meResponse.ok) {
        setVerificationFailed(true);
        return;
      }

      const meData = await meResponse.json();
      const matchedApplication = (meData.applications || []).find(
        (app) =>
          app.applicationNumber === eligibleApplication.applicationNumber &&
          app.lawsuitId === 3 &&
          app.status === "PAYMENT_COMPLETED",
      );

      if (!matchedApplication?.id) {
        setShowNotEligibleModal(true);
        return;
      }

      setIsVerified(true);

      Cookies.set("additionalEvidenceToken", data.token, {
        expires: API_ENDPOINTS.COOKIE_CONFIG.EXPIRES.ACCESS_TOKEN,
        ...API_ENDPOINTS.COOKIE_CONFIG.OPTIONS,
      });

      const resolvedApplicationNumber =
        matchedApplication.applicationNumber ||
        eligibleApplication.applicationNumber;

      Cookies.set(
        "additionalEvidenceApplication",
        JSON.stringify({
          applicationNumber: resolvedApplicationNumber,
          id: matchedApplication.id,
          status: matchedApplication.status,
          lawsuitId: matchedApplication.lawsuitId,
          accessCode:
            matchedApplication.accessCode || eligibleApplication.accessCode,
          phoneNumber: phoneNumber,
        }),
        {
          expires: 1,
          ...API_ENDPOINTS.COOKIE_CONFIG.OPTIONS,
        },
      );

      const documentKeys = [
        "additionalEvidence",
        "additional-evidence",
        "additional_evidence",
      ];
      const hasExistingEvidence = documentKeys.some(
        (key) => matchedApplication.applicationDocuments?.[key]?.fileKey,
      );

      if (hasExistingEvidence && resolvedApplicationNumber) {
        navigateTo(`/status/additional-evidence/${resolvedApplicationNumber}`);
      } else {
        navigateTo("/additionalevidence/done");
      }
    } catch (error) {
      setVerificationFailed(true);
      showToastMessage("인증 처리 중 오류가 발생했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerificationCodeChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
    setVerificationCode(value);
    if (value.length === 6) verificationInputRef.current?.blur();
  };

  const pageVariants = {
    initial: { opacity: 0 },
    in: { opacity: 1, transition: { duration: 0.5 } },
    out: { opacity: 0, transition: { duration: 0.3 } },
  };

  const contentVariants = {
    initial: { opacity: 0, y: 20 },
    in: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: 0.2, staggerChildren: 0.1 },
    },
  };

  const toastVariants = {
    initial: { opacity: 0, y: 50 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, y: 50, transition: { duration: 0.3 } },
  };

  const submitDisabled =
    !isVerificationRequested || verificationCode.length !== 6 || isSubmitting;

  const handleSubmit = () => {
    if (!isVerificationRequested) {
      handleRequestVerification();
      return;
    }
    handleVerify();
  };

  const submitLabel = !isVerificationRequested
    ? isSubmitting
      ? "요청 중..."
      : "인증번호 받기"
    : isSubmitting
      ? "확인 중..."
      : "완료하기";

  const submitButtonDisabled = !isVerificationRequested
    ? !isValidPhone || isSubmitting
    : submitDisabled;

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <AnimatePresence>
            {showToast && (
              <Toast
                variants={toastVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <ToastIcon>!</ToastIcon>
                {toastMessage}
              </Toast>
            )}
          </AnimatePresence>

          <Header>
            <Title>본인 인증하기</Title>
            <Description>
              증거 자료 제출을 위해 휴대폰 본인 인증을 진행해주세요.
            </Description>
          </Header>

          <FormContainer>
            <FormGroup>
              <Label>휴대폰 번호를 입력해주세요</Label>
              <InputContainer>
                <FieldInput
                  type="tel"
                  placeholder="휴대폰 번호를 입력해주세요"
                  value={phoneNumber}
                  onChange={handlePhoneNumberChange}
                  ref={phoneInputRef}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={11}
                  isNumeric
                  disabled={isVerified}
                  readOnly={isVerified}
                  $hasButton={!isVerified}
                  style={
                    isVerified
                      ? { backgroundColor: "#f5f5f5", cursor: "not-allowed" }
                      : {}
                  }
                />
                {!isVerified && isVerificationRequested && (
                  <InlineButtonWrapper>
                    {resendTimer === 0 ? (
                      <InlineButton
                        onClick={handleResendVerification}
                        disabled={isSubmitting}
                      >
                        재전송
                      </InlineButton>
                    ) : (
                      <InlineButtonDisabled>재전송</InlineButtonDisabled>
                    )}
                  </InlineButtonWrapper>
                )}
              </InputContainer>
              {showPhoneFormatError && (
                <PhoneHintError>
                  010으로 시작하는 휴대폰 번호를 입력해주세요.
                </PhoneHintError>
              )}
            </FormGroup>

            {isVerificationRequested && !isVerified && (
              <FormGroup>
                <Label>인증번호를 입력해주세요</Label>
                <InputContainer>
                  <FieldInput
                    type="tel"
                    placeholder="인증번호 6자리를 입력해 주세요"
                    value={verificationCode}
                    onChange={handleVerificationCodeChange}
                    ref={verificationInputRef}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    isNumeric
                    $hasButton
                  />
                  <TimerLabel>{formatTime()}</TimerLabel>
                </InputContainer>
                <Hint>
                  인증번호는 3분간 유효합니다. 인증번호가 오지 않았다면{" "}
                  <ResendHighlight>재전송</ResendHighlight>을 눌러주세요.
                </Hint>
                {verificationFailed && (
                  <VerificationFail>
                    인증에 실패했습니다. 인증번호를 다시 확인해주세요.
                  </VerificationFail>
                )}
              </FormGroup>
            )}

            {errorMessage && !verificationFailed && (
              <VerificationFail>{errorMessage}</VerificationFail>
            )}
          </FormContainer>

          <ButtonsContainer>
            <ActionButton
              onClick={handleSubmit}
              whileHover={{ scale: submitButtonDisabled ? 1 : 1.02 }}
              whileTap={{ scale: submitButtonDisabled ? 1 : 0.98 }}
              disabled={submitButtonDisabled}
            >
              {submitLabel}
            </ActionButton>
          </ButtonsContainer>

          {showRateLimitModal && (
            <ModalOverlay>
              <ModalContainer>
                <ModalContent>
                  <ModalTitle>요청 제한 초과</ModalTitle>
                  <ModalText>{rateLimitMessage}</ModalText>
                  <ModalButton onClick={() => setShowRateLimitModal(false)}>
                    확인
                  </ModalButton>
                </ModalContent>
              </ModalContainer>
            </ModalOverlay>
          )}

          {showNotEligibleModal && (
            <ModalOverlay>
              <ModalContainer>
                <ModalContent>
                  <ModalTitle>신청 내역을 찾을 수 없습니다</ModalTitle>
                  <ModalText>
                    해당 번호로 결제 완료된 듀오 집단소송 신청서를 찾지
                    못했습니다.
                    <br />
                    신청 내역을 다시 확인해주세요.
                  </ModalText>
                  <ModalButton
                    onClick={() => {
                      setShowNotEligibleModal(false);
                      navigateTo("/");
                    }}
                  >
                    확인
                  </ModalButton>
                </ModalContent>
              </ModalContainer>
            </ModalOverlay>
          )}
        </Content>
      </Container>
    </PageBackground>
  );
};

const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f7f7f7;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f7f7f7;

  @media (max-width: 768px) {
    min-height: auto;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
  }

  @media (min-width: 769px) {
    border-radius: 20px;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 60px 20px 80px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 40px;
  color: #333;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 12px;
  word-break: keep-all;
  color: #333;
  text-align: left;
  width: 100%;
`;

const Description = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  margin-bottom: 0;
  word-break: keep-all;
  color: #666;
  text-align: left;
  width: 100%;
`;

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 30px;
`;

const FormGroup = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

const Label = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
  color: #333;
`;

const InputContainer = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  box-sizing: border-box;
`;

const FieldInput = styled.input`
  flex: 1;
  height: 56px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  padding: 0 ${(props) => (props.$hasButton ? "100px" : "16px")} 0 16px;
  font-size: 16px;
  background-color: white;
  width: 100%;
  box-sizing: border-box;
  letter-spacing: ${(props) => (props.isNumeric ? "2px" : "normal")};

  &::placeholder {
    color: #999;
    letter-spacing: normal;
  }

  &:focus {
    outline: none;
    border-color: #e31c20;
  }
`;

const InlineButtonWrapper = styled.div`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
`;

const InlineButton = styled.button`
  display: flex;
  padding: 8px 12px;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  background: #f2f9ff;
  color: #e31c20;
  font-size: 12px;
  font-weight: 400;
  border: none;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const InlineButtonDisabled = styled(InlineButton)`
  color: #b0b3b8;
  background: #f0f0f0;
  font-weight: 400;
  opacity: 0.7;
  cursor: not-allowed;
`;

const TimerLabel = styled.span`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
  color: #e31c20;
  font-weight: 400;
`;

const Hint = styled.p`
  font-size: 13px;
  color: #666;
  margin: 8px 0 0;
`;

const ResendHighlight = styled.span`
  color: #fe7156;
  text-decoration: underline;
  font-weight: 600;
`;

const PhoneHintError = styled.p`
  font-size: 13px;
  color: #ff4c4c;
  margin: 8px 0 0;
`;

const VerificationFail = styled.div`
  width: 100%;
  padding: 10px;
  margin-top: 10px;
  background-color: rgba(255, 76, 76, 0.1);
  color: #ff4c4c;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 40px;
  padding: 0;

  @media (min-width: 769px) {
    max-width: 470px;
  }
`;

const ActionButton = styled(motion.button)`
  display: flex;
  width: 100%;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  border-radius: 20px;
  background: ${(props) => (props.disabled ? "#E0898A" : "#E31C20")};
  color: ${(props) => (props.disabled ? "#DDC2C2" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  border: none;
  transition: background-color 0.2s ease;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  touch-action: none;
`;

const ModalContainer = styled.div`
  width: 90%;
  max-width: 355px;
  background-color: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const ModalContent = styled.div`
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin: 0 0 15px 0;
  width: 100%;
`;

const ModalText = styled.p`
  font-size: 16px;
  color: #333;
  text-align: left;
  margin-bottom: 30px;
  line-height: 1.5;
  width: 100%;
`;

const ModalButton = styled.button`
  width: 100%;
  height: 50px;
  background-color: #e31c20;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
`;

const Toast = styled(motion.div)`
  position: fixed;
  bottom: 30px;
  left: 7%;

  @media (min-width: 768px) {
    left: 38.5%;
    max-width: 400px;
    min-width: 40%;
  }

  transform: translateX(-50%);
  background-color: rgba(46, 56, 64, 0.9);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 14px;
  z-index: 1000;
  min-width: 300px;
  max-width: 90%;
  text-align: left;
`;

const ToastIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background-color: #ff4c4c;
  color: white;
  border-radius: 50%;
  margin-right: 12px;
  font-size: 16px;
  font-weight: bold;
`;

export default AdditionalEvidencePage;
